import { type MaybeRefOrGetter, onBeforeUnmount, onMounted, toValue, watch } from "vue";

import { getConsumer } from "@/lib/cable";

type Identifier = { channel: string } & Record<string, unknown>;

export function useCableSubscription<T>(
  identifier: MaybeRefOrGetter<Identifier>,
  onReceive: (msg: T) => void,
) {
  let unsubscribe: (() => void) | undefined;

  const subscribe = () => {
    const resolvedIdentifier = toValue(identifier);

    console.debug("[CableHook] Subscribing:", resolvedIdentifier);
    const sub = getConsumer().subscriptions.create(resolvedIdentifier, {
      initialized() {
        console.debug("[CableHook] Initialized:", resolvedIdentifier);
      },
      connected() {
        console.debug("[CableHook] Connected:", resolvedIdentifier);
      },
      disconnected() {
        console.debug("[CableHook] Disconnected:", resolvedIdentifier);
      },
      rejected() {
        console.warn("[CableHook] Rejected:", resolvedIdentifier);
      },
      received: (data: T) => {
        console.debug("[CableHook] Received:", resolvedIdentifier, data);
        onReceive(data);
      },
    });

    unsubscribe = () => {
      console.debug("[CableHook] Unsubscribing:", resolvedIdentifier);
      sub.unsubscribe();
      unsubscribe = undefined;
    };
  };

  onMounted(() => {
    subscribe();
  });

  watch(
    () => JSON.stringify(toValue(identifier)),
    () => {
      unsubscribe?.();
      subscribe();
    },
  );

  onBeforeUnmount(() => {
    unsubscribe?.();
  });
}

export function useCableRouter<U extends { type: string }>(
  identifier: MaybeRefOrGetter<Identifier>,
  handlers: { [K in U["type"]]?: (msg: Extract<U, { type: K }>) => void },
) {
  useCableSubscription<U>(identifier, (msg) => {
    const handler = handlers[msg.type as U["type"]] as ((message: U) => void) | undefined;
    handler?.(msg);
  });
}
